import { Text } from '@kk/ui';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { automaticSignIn } from '@/auth/automaticSignIn';
import { inviteTokenFromUrl, pendingInvite } from '@/auth/pendingInvite';
import { useClients, useSession } from '@/auth/SessionProvider';
import { StateCard } from '@/components/StateCard';
import { deck } from '@/copy/deck';
import { placeholders } from '@/copy/placeholders';
import { errorCode } from '@/data/hooks';
import { SignInScreen } from '@/screens/SignInScreen';

type Outcome = 'working' | 'signIn' | 'expired' | 'wrongAddress' | 'failed';

/**
 * `/invites/accept#token=…`, the link the API's staff invite carries and the only club-facing way into
 * this portal: everyone here was invited by their own org admin or by us (`POST /v1/orgs`).
 *
 * **The precondition is an Auth0 sign-in and a valid invite token — nothing else** (Evan, 2026-09-16).
 * This screen used to wait for a STAFF session, and `/v1/org/me` refuses anyone who is not staff yet,
 * which is every person opening their first invite: you could not have a membership until you accepted,
 * and could not accept until you had one. The refusal is right for the shell and wrong as a gate here, so
 * this screen never reads it — `auth.acceptInvite` takes the identity token straight from Auth0.
 *
 * **It sends someone to Auth0 at most once on its own.** Not from a page that is itself a return from
 * Auth0 (whatever failed there has to be drawn — a second trip comes back the same way), and not twice in
 * a tab (`automaticSignIn`). Either refusal draws the sign-in card, whose button is a person asking.
 *
 * The token arrives in a FRAGMENT and the sign-in hop destroys it, so it is parked first; Auth0 returns
 * to the origin, and App routes anyone with a parked token back here.
 *
 * An invite that has run out says so and offers nothing (Evan's ruling, verbatim): there is genuinely
 * nothing this person can do from here, and a button that re-tried it would be a lie.
 */
export function InviteAcceptScreen() {
  const { status } = useSession();
  const { auth } = useClients();
  const navigate = useNavigate();
  const [token] = useState<string | null>(() => {
    const fromUrl = inviteTokenFromUrl(window.location);
    if (fromUrl) {
      pendingInvite.write(fromUrl);
      // a click on the link is a person asking, so it earns its one automatic trip to Auth0 again
      automaticSignIn.reset();
      // out of the address bar, so it is not in history, not in a screenshot and not re-sent on reload
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    return fromUrl ?? pendingInvite.read();
  });
  const [outcome, setOutcome] = useState<Outcome>('working');
  const [signedInAs, setSignedInAs] = useState('');
  const started = useRef(false);

  useEffect(() => {
    // `loading` is only waited out so a callback's code exchange has finished before the token is asked for
    if (!token || status === 'loading' || started.current) return;
    started.current = true;
    void auth.acceptInvite(token).then(
      (session) => {
        if (session) {
          pendingInvite.clear();
          navigate('/approvals', { replace: true });
          return;
        }
        // nobody is signed in at all: they have to be somebody before they can be invited
        if (auth.kind !== 'redirect') {
          navigate('/sign-in', { replace: true });
          return;
        }
        if (!auth.landedFromCallback && automaticSignIn.take()) {
          void auth.beginRedirect?.();
          return;
        }
        console.error(
          '[kk portal] no Auth0 session after',
          auth.landedFromCallback
            ? 'returning from Auth0'
            : 'the one automatic sign-in this tab gets',
        );
        setOutcome('signIn');
      },
      async (e: unknown) => {
        console.error('[kk portal] accepting the invite failed:', e);
        /*
         * One attempt, and the token is dropped either way. A used invite is used; and the wrong-account
         * state is fixed by signing out and OPENING THE LINK AGAIN (the ruled copy says so), so a token
         * parked here would attach itself to whichever account signed in next.
         */
        pendingInvite.clear();
        const code = errorCode(e);
        if (code === 'FORBIDDEN') setSignedInAs((await auth.currentEmail()) ?? '');
        setOutcome(
          code === 'NOT_FOUND' || code === 'INVALID_STATE'
            ? 'expired'
            : code === 'FORBIDDEN'
              ? 'wrongAddress'
              : 'failed',
        );
      },
    );
  }, [token, status, auth, navigate]);

  /*
   * A link with no token at all is not an expired invite (the 23 Oct audit, T2-15): the link was cut short on its way
   * here. It says so, and Sign in is the way on, rather than a dead end that blames the invite.
   */
  if (!token)
    return (
      <StateCard
        body={deck.states.inviteIncomplete}
        cta={{ label: deck.signIn.cta, onPress: () => navigate('/sign-in', { replace: true }) }}
        testID="invite-incomplete"
      />
    );
  if (outcome === 'expired')
    return (
      <StateCard
        title={deck.states.inviteExpired.title}
        body={deck.states.inviteExpired.body}
        testID="invite-expired"
      />
    );
  if (outcome === 'signIn') return <SignInScreen />;
  if (outcome === 'wrongAddress')
    return (
      <StateCard
        title={deck.states.inviteWrongAccount.title}
        body={deck.states.inviteWrongAccount.body(signedInAs)}
        cta={{ label: deck.states.inviteWrongAccount.cta, onPress: () => void auth.signOut() }}
        testID="invite-wrong-address"
      />
    );
  if (outcome === 'failed')
    return (
      <StateCard
        title={deck.states.signInFailed.title}
        body={deck.states.signInFailed.body}
        cta={{ label: deck.states.signInFailed.cta, onPress: () => window.location.reload() }}
        testID="invite-failed"
      />
    );
  return (
    <div className="sign-in">
      <div className="sign-in-card">
        <Text variant="bodySm" tone="slate" testID="invite-working">
          {placeholders.invite.accepting}
        </Text>
      </div>
    </div>
  );
}
